import { useState } from "react";
import { formatTime } from "../lib/day.ts";
import type { Entry, EntryInput, Mood } from "../lib/types.ts";
import { EntryForm } from "./EntryForm.tsx";

const SOURCE_LABEL: Record<Entry["source"], string> = {
  web: "Web",
  api: "API",
  mcp: "Agente",
  device: "Dispositivo",
};

type Props = {
  entries: Entry[];
  moods: Mood[];
  /**
   * Sin estas dos la lista es de solo lectura, que es lo que ve el rol
   * `viewer`: los registros, sin los botones de editar y borrar.
   */
  onUpdate?: (id: string, input: Partial<EntryInput>) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
};

export function EntryList({ entries, moods, onUpdate, onDelete }: Props) {
  const [editing, setEditing] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const editable = Boolean(onUpdate && onDelete);

  return (
    <ul className="entries">
      {entries.map((entry) => {
        if (onUpdate && editing === entry.id) {
          return (
            <li key={entry.id} className="entry entry--editing">
              <EntryForm
                moods={moods}
                entry={entry}
                allowDate
                submitLabel="Guardar cambios"
                onSubmit={async (input) => {
                  await onUpdate(entry.id, input);
                  setEditing(null);
                }}
                onCancel={() => setEditing(null)}
              />
            </li>
          );
        }

        return (
          <li key={entry.id} className="entry">
            <div className="entry__mood" aria-hidden="true">
              {entry.mood.emoji}
            </div>

            <div className="entry__body">
              <div className="entry__head">
                <strong>{entry.mood.label}</strong>
                <span
                  className="entry__energy"
                  title={`Energía: ${entry.energy} de 100`}
                >
                  {entry.energy}
                </span>
                <span className="spacer" />
                <span className="subtle">{formatTime(entry.loggedAt)}</span>
              </div>

              {entry.note && <p className="entry__note">{entry.note}</p>}

              <div className="entry__meta">
                <span className="pill">
                  {SOURCE_LABEL[entry.source]}
                  {entry.sourceLabel ? ` · ${entry.sourceLabel}` : ""}
                </span>

                <span className="spacer" />

                {editable &&
                  (confirming === entry.id ? (
                    <>
                      <span className="subtle">¿Seguro?</span>
                      <button
                        className="btn btn--quiet btn--danger"
                        onClick={() => void onDelete?.(entry.id)}
                      >
                        Sí, borrar
                      </button>
                      <button
                        className="btn btn--quiet"
                        onClick={() => setConfirming(null)}
                      >
                        No
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="btn btn--quiet"
                        onClick={() => setEditing(entry.id)}
                      >
                        Editar
                      </button>
                      <button
                        className="btn btn--quiet btn--danger"
                        onClick={() => setConfirming(entry.id)}
                      >
                        Borrar
                      </button>
                    </>
                  ))}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
